/* eslint-disable react-hooks/exhaustive-deps */
import { ReactNode, createContext, useCallback, useEffect } from 'react';
import { useIdle, useSessionStorage } from '@uidotdev/usehooks';
import { getToken, getTokenByEmail, isDummyLogin } from 'services/api/utils';
import { useNavigate } from 'react-router-dom';
import { useWeb3 } from './Web3Context';
import { useUI } from 'contexts/UiContext';
import { useTranslation } from 'react-i18next';
import { useFloatNotification } from 'contexts/FloatNotificationContext';
import { ROUTES } from 'routes/routes';

type AuthContextType = {
  isAuthenticated: boolean;
  login: (nonce: string, sig: string) => Promise<void>;
  loginWithEmail: (email: string) => Promise<void>;
  logout: () => Promise<void>;
};

export const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  login: async () => {},
  loginWithEmail: async () => {},
  logout: async () => {},
});

type AuthProviderProps = {
  children: ReactNode;
};

function extractToken(resp: any): string {
  if (typeof resp === 'string') return resp;
  if (resp?.data && typeof resp.data === 'string') return resp.data;
  if (resp?.data?.data && typeof resp.data.data === 'string')
    return resp.data.data;
  if (resp?.token && typeof resp.token === 'string') return resp.token;
  throw new Error('Unexpected login response shape');
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const dummyLogin = isDummyLogin();
  const { openNotification } = useFloatNotification();
  const { t } = useTranslation();
  const { clearUIState } = useUI();
  const navigate = useNavigate();
  const { disconnect } = useWeb3();
  const isIdle = useIdle(
    parseInt(process.env.REACT_APP_INACTIVITY_MINUTES ?? '60') * 60 * 1000
  );

  const [isAuthenticated, setIsAuthenticated] = useSessionStorage(
    'isAuthenticated',
    false
  );

  const login = useCallback(
    async (nonce: string, sig: string) => {
      if (dummyLogin) return;
      const resp = await getToken(nonce, sig);
      const token = extractToken(resp);
      localStorage.setItem('accessDarwinToken', token);
      setIsAuthenticated(true);
    },
    [setIsAuthenticated]
  );

  const loginWithEmail = useCallback(
    async (email: string) => {
      const resp = await getTokenByEmail(email);
      const token = extractToken(resp);

      localStorage.setItem('accessDarwinToken', token);
      setIsAuthenticated(true);
    },
    [setIsAuthenticated]
  );

  const logout = useCallback(async () => {
    try {
      setIsAuthenticated(false);
      localStorage.removeItem('accessDarwinToken');
      await disconnect();
      clearUIState();
      navigate(ROUTES.HOME);
      window.location.reload();
    } catch {
      openNotification(t('error'), t('logout.logout-error'), 'error');
    }
  }, [disconnect, clearUIState, navigate, openNotification, t]);

  useEffect(() => {
    if (isIdle) logout();
  }, [isIdle, logout]);

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, login, loginWithEmail, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
};
